/**
 * NIZAM ONE ERP — Module Service (Batch 4 / PG-007)
 * Provides a cached, company-aware module enablement check.
 * Cache TTL: 60 seconds (modules rarely change; invalidate on mutation).
 */
import type { ModuleDefinition, ModuleStatus } from '../queries/moduleActivation';
import { getCompanyModuleStatuses } from '../queries/moduleActivation';

const CACHE_TTL_MS = 60_000;

interface CacheEntry {
  data: ModuleStatus[];
  expiresAt: number;
}

// In-memory cache per company
const cache = new Map<string, CacheEntry>();

// Core modules are always enabled regardless of DB state
const CORE_MODULES = new Set(['platform', 'admin', 'rbac', 'audit', 'notifications', 'approvals']);

export async function loadCompanyModules(companyId: string): Promise<ModuleStatus[]> {
  const cached = cache.get(companyId);
  if (cached && Date.now() < cached.expiresAt) return cached.data;

  const data = await getCompanyModuleStatuses(companyId);
  cache.set(companyId, { data, expiresAt: Date.now() + CACHE_TTL_MS });
  return data;
}

export function clearModuleCache(companyId?: string): void {
  if (companyId) {
    cache.delete(companyId);
  } else {
    cache.clear();
  }
}

export function getCachedCompanyModules(companyId: string): ModuleStatus[] | null {
  const cached = cache.get(companyId);
  if (cached && Date.now() < cached.expiresAt) return cached.data;
  return null;
}

/**
 * Returns true if a module is effectively enabled for the company.
 * Core modules are always true. Falls back to definition default if no override.
 * If modules haven't loaded yet, fails open (true) to avoid blocking UI.
 */
export function isModuleEnabled(
  moduleKey: string,
  modules: ModuleStatus[] | null,
): boolean {
  if (CORE_MODULES.has(moduleKey)) return true;
  if (!modules) return true; // fail-open during loading

  const m = modules.find(m => m.module_key === moduleKey);
  if (!m) return true; // unknown module — don't block
  return m.effective_enabled;
}

export function getModuleDefinitions(modules: ModuleStatus[]): ModuleDefinition[] {
  return modules;
}
